"use client";

/**
 * Renders an instructor-authored HTML/CSS/JS widget in an ORIGIN-ISOLATED
 * sandbox.
 *
 * sandbox="allow-scripts" WITHOUT allow-same-origin gives the iframe a unique
 * opaque origin: the widget's JS can run, draw, and accept input, but it CANNOT
 * read the parent's session token/cookies, call our bearer-gated API, or touch
 * the parent/Canvas frame. Never add allow-same-origin alongside allow-scripts
 * (that lets the frame drop its own sandbox).
 *
 * Sizing: by default the frame auto-fits its content — an injected
 * ResizeObserver posts the document height and we resize the iframe (messages
 * are trusted only when event.source is THIS iframe's window; the opaque
 * origin means we can't filter by origin). An explicit `height` is a fixed
 * override set in the activity's builder.
 */

import { useEffect, useRef, useState } from "react";

const MIN_HEIGHT = 80;
const MAX_HEIGHT = 2000;

const RESIZE_SCRIPT = `(function(){
  var post=function(){
    var h=Math.max(document.documentElement.scrollHeight,document.body?document.body.scrollHeight:0);
    parent.postMessage({cfFrameHeight:h},"*");
  };
  if(window.ResizeObserver){new ResizeObserver(post).observe(document.documentElement);}
  window.addEventListener("load",post);setTimeout(post,50);setTimeout(post,500);
})();`;

export default function JsInteractive({
  html,
  css,
  js,
  height,
}: {
  html?: string;
  css?: string;
  js?: string;
  height?: number;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [autoHeight, setAutoHeight] = useState(160);
  const fixed = typeof height === "number" && height > 0;

  useEffect(() => {
    if (fixed) return;
    const onMessage = (event: MessageEvent) => {
      // Opaque-origin frames send origin "null" — identify ours by source
      if (event.source !== frameRef.current?.contentWindow) return;
      const reported = Number((event.data as { cfFrameHeight?: unknown })?.cfFrameHeight);
      if (!Number.isFinite(reported) || reported <= 0) return;
      setAutoHeight(Math.min(Math.max(Math.ceil(reported) + 2, MIN_HEIGHT), MAX_HEIGHT));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [fixed]);

  const srcDoc = `<!doctype html><html><head><meta charset="utf-8">
<style>:root{color-scheme:light dark}body{margin:0;font-family:system-ui,sans-serif;padding:8px}
${css ?? ""}</style></head><body>${html ?? ""}<script>${js ?? ""}<\/script>${
    fixed ? "" : `<script>${RESIZE_SCRIPT}<\/script>`
  }</body></html>`;

  return (
    <iframe
      ref={frameRef}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      title="Custom interactive"
      data-annotate="off"
      className="not-prose my-4 w-full rounded-lg border border-line"
      style={{ height: fixed ? height : autoHeight, transition: "height 0.15s ease" }}
      // The browser normalizes the srcdoc attribute string on parse, so a
      // server-rendered iframe's srcdoc never byte-matches on hydration. The
      // content is identical; suppress the spurious attribute mismatch.
      suppressHydrationWarning
    />
  );
}
